import { supertypeClass, property } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';

import _ from 'lodash';
import { Customer } from './Customer';
import { Policy } from './Policy';
import { Counter } from '../Counter';
import { HitAppCtx, HitAppExecutionCtx } from '../orca/HitAppCtx';
import { WorkflowNames } from '../orca/WorkflowName';
import { ApplicationResponse } from '@hit/hit-api-client';
import { ExamOrderResponse } from '../../../common/js/examServices/ExamSchedulingStatus';
import { ExamVendor } from '../interfaces/CommonTypes';
import { BaseQuote } from '../quote/BaseQuote';
import { ApplicationTypes } from '../../../coverpath-common/js/interfaces/CoverpathTypes';
import { POLICY_TYPES } from '../constants/policy';

export enum ApplicationSourceType {
  GENERIC_LINK = 'genericLink',
  PERSONALIZED_QUOTE = 'personalizedQuote',
  START_AGENT_ASSISTED = 'startAAA',
  SEND_AGENT_ASSISTED = 'sendAAA',
  REAPPLY = 'reapply',
  DUPLICATE = 'duplicate',
  ADVISOR_EDIT = 'advisorEdit',
  MULTIPLE_POLICIES = 'multiplePolicies',
  ADVISOR_DRIVEN_ANNUITY_APPLICATION = 'advisorDrivenAnnuityApplication',
  TERM_CONVERSION = 'termConversion',
}

@supertypeClass({ toClient: true, toServer: true })
export class Application extends Everything implements ApplicationResponse {
  static async fetchPrimaryPolicyById(appId: string, fetchSpec?: { fetch: {} }): Promise<Policy | undefined> {
    const app = (await Application.persistorFetchById(appId, fetchSpec)) as Application;
    return app && app.primaryPolicy;
  }

  @property({ getType: () => Customer })
  customer: Customer;

  // Some applications are marked by algorithmic underwriting as part of the Instant Term Mainstream marketing
  // program. These policies are capped at low face amounts but have increased eligibility for accelerated issue.
  @property()
  isITM: boolean;

  @property()
  examVendor: string;

  @property({
    comment: `Used by CAS policies, this is the date when agent puts the application on hold. Absense of this date means application is not on hold`,
  })
  holdTime: Date;

  @property({
    comment: 'this is set when the offer is accepted',
  })
  finalOfferAcceptedDate: Date;

  @property({ comment: 'this is set when the offer is sent to the customer' })
  finalOfferSentDate: Date;

  @property({ getType: () => BaseQuote, comment: 'Quote from which this application was created.' })
  quote: BaseQuote;

  @property({ isLocal: true })
  insuredHasAcknowledged: boolean;

  @property({ isLocal: true })
  ownerHasAcknowledged: boolean;

  @property({ isLocal: true })
  otherOwnerHasAcknowledged: boolean;

  @property({
    comment: `A flag to determine if DocuSign form were retrieved`,
  })
  isDocuSignedFormRetrieved: boolean = false;

  @property({
    comment: `this flag is used to indicate that the policy should wait in uw2
        (second time) for the purpose of holding up a policy for replacement
        review`,
    toClient: true,
    toServer: true,
  })
  isFrozen: boolean;

  /*
  This field indicates how the application was created.
  Examples: see ApplicationSourceType
   */
  @property({
    comment: 'How the application was created',
  })
  sourceType: ApplicationSourceType;

  /*
  If the application was created through cloning another application, this field is the original application's applicationNumber.
  If the application was created through links (generic, personalized, AAA), this field is null
   */
  @property({
    comment: 'The applicationNumber of the original application from which this one was created',
  })
  sourceAppNumber: string;

  /*
  For the first application, original application number will be null.
  If an application is duplicated or edited, the same original application number will be recorded for all subsequent apps.
  */
  @property({
    comment: 'The applicationNumber of the original application that this application traces all the way back to',
  })
  originalApplicationNumber: string;

  @property()
  examOrderResponse: ExamOrderResponse;

  @property()
  haveExamVendorCallClient: boolean;

  @property()
  examOrderedDate: Date;

  constructor() {
    super();
  }

  static new(customer) {
    return new Application().init(customer);
  }

  init(customer) {
    this.customer = customer;
    this.type = ApplicationTypes.ACTIVE;
    this.createdTime = new Date();
    this.customer.registerPostServerCallCallback(() => this.startFailureNotificationWorkflow());
    return this;
  }

  isCasApplication(): boolean {
    return this.constructor.name === 'CasApplication';
  }

  addPrimaryPolicy(policy: Policy) {
    this.policies.forEach((pol) => {
      if (pol.isPrimary) {
        pol.type = '';
      }
    });
    policy.type = POLICY_TYPES.PRIMARY;
    this.policies.push(policy);

    /* This is needed to ensure all the child objects are synced.
        This only happens with a setter, so it wasn't needed when we were setting applicationPolicy.
        Now we just push the policy into an array which doesn't do the same thing so objects like beneficiary
        are being created but not available on the client side until refresh. */
    this.policies = this.policies;
  }

  /**
   * override this.
   * consumes an application and returns the current application with
   * relevant properties copied.
   * @param application
   * @returns {Application}
   */
  async cloneApplicationProps(application: Application, isAppSubmitted: boolean): Promise<Application> {
    return this;
  }

  async createClonedApplicationAsync(): Promise<Application> {
    let newApp = this.getApplicationType().new(this.customer);

    const isAppSubmitted = !!this.primaryPolicy.workflowState;
    // copy over relevant application properties based on which flavor of application this is.
    newApp = await newApp.cloneApplicationProps(this, isAppSubmitted);
    newApp.examVendor = this.examVendor;

    // this is required for clone policy to work
    // Clone all policies, e.g., clone both policies for joint app
    const policies = await Promise.all(this.policies.map((policy) => policy.fetchAndCreateClonedPolicy()));

    newApp.policies = policies;

    this.updatePrimaryCustomerData(this, newApp);

    return newApp;
  }

  protected updatePrimaryCustomerData(oldApplication: Application, newApplication: Application): void {
    const primaryCustomer = newApplication.primaryPolicy.insured;

    primaryCustomer.overrideAmount = oldApplication.primaryPolicy.faceAmount;
    primaryCustomer.overrideTerm = oldApplication.primaryPolicy.term;
    if (primaryCustomer.person) {
      primaryCustomer.person.email = oldApplication.primaryPolicy.insured.person.email;
    }
  }

  assignApplicationNumber(): Promise<string> {
    const setAppNum = (appNum): string => {
      return (this.applicationNumber = String(appNum));
    };

    return Counter.getNextNumber(Counter.applicationNumber).then(setAppNum);
  }

  startWorkflow(firstStage) {
    return HitAppCtx.get.startNewExecution(async (exnCtx) => {
      await HitAppCtx.get.policyClient.createApplicationWorkflows(this);
      (exnCtx as HitAppExecutionCtx).workflowName = WorkflowNames.getDefaultWorkflow();
      return HitAppCtx.get.policyClient.queueStartWorkflow(this, undefined, firstStage);
    }, HitAppCtx.newBaseMsgCtx(this.customer._id));
  }

  startFailureNotificationWorkflow() {
    return HitAppCtx.get.failureNotificationClient.createWorkflow(this);
  }

  startConversionWorkflow() {
    return HitAppCtx.get.startNewExecution(async (exnCtx) => {
      await HitAppCtx.get.policyClient.createApplicationWorkflows(this);
      (exnCtx as HitAppExecutionCtx).workflowName = WorkflowNames.AUTOMATED_CONVERSION.name;
      return HitAppCtx.get.conversionWorkflow.createConversionWorkflow(this);
    }, HitAppCtx.newBaseMsgCtx(this.customer._id));
  }

  get secondaryPolicy(): Policy {
    return _.find(this.policies, (policy) => {
      return policy.type === POLICY_TYPES.SECONDARY;
    });
  }

  // Copied from ApplicationCore
  @property({ type: Array, getType: () => Policy })
  policies: Array<Policy> = [];

  @property()
  applicationNumber: string;

  @property()
  type: string;

  @property()
  createdTime: Date;

  @property({ comment: 'Is this an agent assisted application' })
  isAgentAssistant: boolean = false;

  @property()
  completedAnimationViewedDate: Date;

  get primaryPolicy(): Policy {
    return (this.policies || []).reduce((priPol, pol) => {
      return priPol || (pol.isPrimary ? pol : null);
    }, null);
  }
  // END Copied from ApplicationCore

  public getApplicationType() {
    return Application;
  }

  getTlicTotal() {
    return this.primaryPolicy.tLICPremiumBreakdown.getTotal().toFixed(2);
  }

  /*
   * This function returns true after a policy has gone through AU
   * and when evaluating its rule results is determined to be LiteTouch.
   */
  isLiteTouch(): boolean {
    const LITETOUCH_ELIGIBLE = '50';
    const LITETOUCH_POSSIBLE_DECLINE = '995';

    // We still need to handle old policies which may call this function in specific WF activities (AM3A1)
    // The source of truth for AU rateclass for these policies is on the UW side (where they are migrated)
    const isLiteTouch =
      [LITETOUCH_ELIGIBLE, LITETOUCH_POSSIBLE_DECLINE].includes(this.primaryPolicy.acceleratedRateClass) ||
      this.primaryPolicy.isLiteTouch;
    return isLiteTouch;
  }

  async fulfillFailureNotificationRequirements(requirements: string[]) {
    return HitAppCtx.get.failureNotificationClient.fulfillRequirements(requirements, this);
  }

  async fulfillConversionWorkflowRequirements(requirements: string[]) {
    return HitAppCtx.get.conversionWorkflow.fulfillRequirements(requirements, this);
  }

  getExamVendor(): string {
    if (this.examVendor) {
      return this.examVendor;
    } else if (this.primaryPolicy.servicingAgent) {
      return this.primaryPolicy.servicingAgent.getExamVendor();
    }
    return ExamVendor.ExamOne;
  }

  getExamVendorFromAgent(): string {
    if (this.primaryPolicy.primaryPolicyAgent.primaryAgent) {
      return this.primaryPolicy.primaryPolicyAgent.primaryAgent.getExamVendor();
    } else if (this.primaryPolicy.servicingAgent) {
      return this.primaryPolicy.servicingAgent.getExamVendor();
    }
    return ExamVendor.ExamOne;
  }

  // Overriden in CasApplication
  getTotalFaceAmount(): number | null {
    return this.primaryPolicy.faceAmount;
  }

  getTotalFaceAmountWithRTR(): number {
    return this.getTotalFaceAmount() + (this.primaryPolicy.rtrRider ? this.primaryPolicy.rtrRider.amount : 0);
  }

  // Overriden in CasApplication
  isJointApp(): boolean {
    return false;
  }

  // Overriden in CasApplication
  isJointAppWithPendingAmendment() {
    return false;
  }

  getTotalFaceAmountWithPendingAmendment(): number {
    return this.primaryPolicy.faceAmount;
  }

  getTotalPremiumWithPendingAmendment(): number {
    return this.primaryPolicy.premiumBreakdown.getTotal();
  }

  get secondaryPolicyWithPendingAmendment() {
    return null;
  }

  get primaryPolicyWithPendingAmendment() {
    return this.primaryPolicy;
  }
}
